import { css } from '@emotion/react';
import {
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { cx } from './helpers';

type OTPInputProps = {
  length: number;
  value: string;
  onValueChange: (value: string) => void;
  subText?: string;
  subTextFlavor?: 'error' | 'success';
  autoFocus?: boolean;
};

export function OTPInput({
  length,
  value,
  onValueChange,
  subText,
  subTextFlavor,
  autoFocus,
}: OTPInputProps): JSX.Element {
  const inputsRef = useRef<(HTMLInputElement | null)[]>(new Array(length));

  const handleChange = useCallback(
    (element: HTMLInputElement, index: number) => {
      const { value: inputValue } = element;
      const newOtp = value.split('');
      newOtp[index] = inputValue;
      onValueChange(newOtp.join(''));
      if (index < length - 1 && inputValue) {
        inputsRef.current[index + 1]?.focus();
      }
    },
    [length, value, onValueChange],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      const pasteData = event.clipboardData
        .getData('text')
        .replace(/[^0-9]/g, '')
        .slice(0, length);
      onValueChange(pasteData);
      event.preventDefault();
    },
    [length, onValueChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        onValueChange(value.slice(0, -1));
      }
    },
    [value, onValueChange],
  );

  // Focus always sits on the first empty box, so typing fills the code left to right.
  const hasSomeFocus = useRef(false);
  const onFocus = useCallback(() => {
    inputsRef.current[value.length]?.focus();
    hasSomeFocus.current = true;
  }, [value]);
  const onBlur = useCallback(() => {
    hasSomeFocus.current = false;
  }, []);
  useEffect(() => {
    if (hasSomeFocus.current) {
      inputsRef.current[value.length]?.focus();
    }
  }, [value]);

  return (
    <div>
      <fieldset className="aura--otp-input-fieldset">
        {new Array(length).fill('').map((_, index) => (
          <input
            key={index}
            className="aura--otp-input"
            type="text"
            maxLength={1}
            value={value[index] || ''}
            onChange={e => handleChange(e.target, index)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            ref={el => {
              inputsRef.current[index] = el;
            }}
            tabIndex={index === 0 ? 0 : -1}
            autoFocus={autoFocus && index === 0}
          />
        ))}
      </fieldset>
      {subText && (
        <div
          className={cx('aura--input-subtext', {
            'aura--error-label': subTextFlavor === 'error',
            'aura--success-label': subTextFlavor === 'success',
          })}
          css={css({ marginLeft: 0, marginTop: 8 })}
        >
          {subText}
        </div>
      )}
    </div>
  );
}
